import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { format, subDays, parseISO } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Calendar } from '../components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'
import {
  BarChart3, PieChart, TrendingUp, Calendar as CalendarIcon,
  CheckCircle2, Clock, AlertTriangle, ListTodo, ArrowRight,
  Loader2, Filter, Target, Zap
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Colors for charts
const STATUS_COLORS = {
  'Pendiente': '#3b82f6',
  'En progreso': '#f59e0b',
  'Terminado': '#10b981',
  'Vencida': '#ef4444'
}

const URGENCY_COLORS = {
  'URGENTE': '#ef4444',
  'MEDIO': '#f59e0b',
  'BAJO': '#22c55e'
}

export default function TaskAnalyticsPage() {
  const { t, locale } = useLanguage()
  const navigate = useNavigate()
  const dateLocale = locale === 'es-MX' ? es : enUS
  
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  
  // Filters
  const [fromDate, setFromDate] = useState(subDays(new Date(), 30))
  const [toDate, setToDate] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)) // +30 days
  const [statusFilter, setStatusFilter] = useState([])
  const [urgencyFilter, setUrgencyFilter] = useState([])
  const [dateField, setDateField] = useState('due')

  const fetchAnalytics = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (fromDate) params.append('from_date', format(fromDate, 'yyyy-MM-dd'))
      if (toDate) params.append('to_date', format(toDate, 'yyyy-MM-dd'))
      if (statusFilter.length > 0) params.append('status', statusFilter.join(','))
      if (urgencyFilter.length > 0) params.append('urgency', urgencyFilter.join(','))
      params.append('date_field', dateField)
      
      const response = await axios.get(`${API}/tasks/analytics?${params.toString()}`)
      setData(response.data)
    } catch (error) {
      console.error('Error fetching analytics:', error)
      toast.error(t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [fromDate, toDate, statusFilter, urgencyFilter, dateField, t])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  const toggleStatus = (status) => {
    setStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    )
  }

  const toggleUrgency = (urgency) => {
    setUrgencyFilter(prev => 
      prev.includes(urgency) 
        ? prev.filter(u => u !== urgency)
        : [...prev, urgency]
    )
  }

  // Simple bar chart using CSS
  const BarChart = ({ items, colorMap, labelKey, maxValue }) => {
    const max = maxValue || Math.max(...items.map(i => i.count), 1)
    return (
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>{item[labelKey]}</span>
              <span className="font-medium">{item.count}</span>
            </div>
            <div className="h-3 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${(item.count / max) * 100}%`,
                  backgroundColor: colorMap[item[labelKey]] || '#6b7280'
                }}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Simple donut chart using CSS
  const DonutChart = ({ items, colorMap, labelKey }) => {
    const total = items.reduce((sum, i) => sum + i.count, 0)
    if (total === 0) {
      return (
        <div className="flex flex-col items-center py-4">
          <div className="w-24 h-24 rounded-full bg-secondary flex items-center justify-center">
            <span className="text-muted-foreground text-sm">0</span>
          </div>
        </div>
      )
    }
    
    let cumulativePercent = 0
    const gradientStops = items.map(item => {
      const percent = (item.count / total) * 100
      const start = cumulativePercent
      cumulativePercent += percent
      return `${colorMap[item[labelKey]] || '#6b7280'} ${start}% ${cumulativePercent}%`
    }).join(', ')
    
    return (
      <div className="flex flex-col items-center py-2">
        <div 
          className="w-24 h-24 rounded-full relative"
          style={{
            background: `conic-gradient(${gradientStops})`
          }}
        >
          <div className="absolute inset-3 bg-background rounded-full flex items-center justify-center">
            <span className="font-bold text-lg">{total}</span>
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-3">
          {items.filter(i => i.count > 0).map((item, idx) => (
            <div key={idx} className="flex items-center gap-1 text-xs">
              <div 
                className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                style={{ backgroundColor: colorMap[item[labelKey]] }}
              />
              <span className="whitespace-nowrap">{item[labelKey]}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Line chart using CSS (simplified)
  const TimelineChart = ({ data: timeData }) => {
    if (!timeData || timeData.length === 0) return null
    
    const maxVal = Math.max(
      ...timeData.map(d => Math.max(d.created, d.done, d.due)),
      1
    )
    
    // Show last 14 days for readability
    const chartData = timeData.slice(-14)
    
    return (
      <div className="space-y-4">
        <div className="flex gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>{t('tasks.created')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span>{t('tasks.done')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span>{t('tasks.due')}</span>
          </div>
        </div>
        
        <div className="flex items-end gap-1 h-32">
          {chartData.map((day, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center gap-0.5">
              <div className="flex gap-px w-full justify-center" style={{ height: '100px' }}>
                <div 
                  className="w-2 bg-blue-500 rounded-t transition-all"
                  style={{ height: `${(day.created / maxVal) * 100}%` }}
                  title={`${t('tasks.created')}: ${day.created}`}
                />
                <div 
                  className="w-2 bg-emerald-500 rounded-t transition-all"
                  style={{ height: `${(day.done / maxVal) * 100}%` }}
                  title={`${t('tasks.done')}: ${day.done}`}
                />
                <div 
                  className="w-2 bg-amber-500 rounded-t transition-all"
                  style={{ height: `${(day.due / maxVal) * 100}%` }}
                  title={`${t('tasks.due')}: ${day.due}`}
                />
              </div>
              <span className="text-[10px] text-muted-foreground">
                {format(parseISO(day.date), 'd', { locale: dateLocale })}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const { kpis, by_status, by_urgency, time_series, top_items } = data || {}

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-600 to-purple-700 text-white px-4 py-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          {t('tasks.analyticsTitle')}
        </h1>
        <p className="text-violet-200 mt-1">
          {format(fromDate, 'd MMM', { locale: dateLocale })} - {format(toDate, 'd MMM yyyy', { locale: dateLocale })}
        </p>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium text-sm">{t('common.filter')}</span>
            </div>
            
            <div className="space-y-3">
              {/* Date Range */}
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="flex-1 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(fromDate, 'd MMM', { locale: dateLocale })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={fromDate}
                      onSelect={(date) => date && setFromDate(date)}
                      locale={dateLocale}
                    />
                  </PopoverContent>
                </Popover>
                
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="flex-1 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(toDate, 'd MMM', { locale: dateLocale })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={toDate}
                      onSelect={(date) => date && setToDate(date)}
                      locale={dateLocale}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Date field toggle */}
              <div className="flex gap-2">
                <Button
                  variant={dateField === 'due' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDateField('due')}
                  className="flex-1"
                >
                  {t('tasks.filterByDue')}
                </Button>
                <Button
                  variant={dateField === 'created' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDateField('created')}
                  className="flex-1"
                >
                  {t('tasks.filterByCreated')}
                </Button>
              </div>
              
              {/* Status chips */}
              <div className="flex flex-wrap gap-2">
                {['Pendiente', 'En progreso', 'Terminado', 'Vencida'].map(status => (
                  <Badge
                    key={status}
                    variant={statusFilter.includes(status) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    style={statusFilter.includes(status) ? { backgroundColor: STATUS_COLORS[status] } : {}}
                    onClick={() => toggleStatus(status)}
                  >
                    {status}
                  </Badge>
                ))}
              </div>
              
              {/* Urgency chips */}
              <div className="flex flex-wrap gap-2">
                {['URGENTE', 'MEDIO', 'BAJO'].map(urgency => (
                  <Badge
                    key={urgency}
                    variant={urgencyFilter.includes(urgency) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    style={urgencyFilter.includes(urgency) ? { backgroundColor: URGENCY_COLORS[urgency] } : {}}
                    onClick={() => toggleUrgency(urgency)}
                  >
                    {urgency}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-blue-600">
                <ListTodo className="w-5 h-5" />
                <span className="text-sm font-medium">{t('tasks.total')}</span>
              </div>
              <p className="text-3xl font-bold mt-2">{kpis?.total || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">{t('tasks.done')}</span>
              </div>
              <p className="text-3xl font-bold mt-2">{kpis?.done || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-amber-600">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">{t('tasks.pending')}</span>
              </div>
              <p className="text-3xl font-bold mt-2">{(kpis?.pending || 0) + (kpis?.doing || 0)}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-500/10 to-red-600/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm font-medium">{t('tasks.overdue')}</span>
              </div>
              <p className="text-3xl font-bold mt-2">{kpis?.overdue || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Completion Rate */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-violet-600" />
                <span className="font-medium">{t('tasks.completionRate')}</span>
              </div>
              <span className="text-2xl font-bold text-violet-600">{kpis?.completion_rate || 0}%</span>
            </div>
            <div className="h-3 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-violet-500 to-purple-600 rounded-full transition-all duration-700"
                style={{ width: `${kpis?.completion_rate || 0}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Charts Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* By Status */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <PieChart className="w-4 h-4" />
                {t('tasks.byStatus')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              {by_status && <DonutChart items={by_status} colorMap={STATUS_COLORS} labelKey="status" />}
            </CardContent>
          </Card>

          {/* By Urgency */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="w-4 h-4" />
                {t('tasks.byUrgency')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {by_urgency && <BarChart items={by_urgency} colorMap={URGENCY_COLORS} labelKey="urgency" />}
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {t('tasks.timeline')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {time_series && <TimelineChart data={time_series} />}
          </CardContent>
        </Card>

        {/* Top Items */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Overdue */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-4 h-4" />
                {t('tasks.overdueTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {top_items?.overdue?.length > 0 ? (
                <div className="space-y-2">
                  {top_items.overdue.map(task => (
                    <div 
                      key={task.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-red-500/10 cursor-pointer hover:bg-red-500/20 transition-colors"
                      onClick={() => navigate('/tasks')}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(parseISO(task.due_date), 'd MMM', { locale: dateLocale })}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('tasks.noData')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Next Due */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-amber-600">
                <Clock className="w-4 h-4" />
                {t('tasks.nextDue')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {top_items?.next_due?.length > 0 ? (
                <div className="space-y-2">
                  {top_items.next_due.map(task => (
                    <div 
                      key={task.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-amber-500/10 cursor-pointer hover:bg-amber-500/20 transition-colors"
                      onClick={() => navigate('/tasks')}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(parseISO(task.due_date), 'd MMM', { locale: dateLocale })}
                        </p>
                      </div>
                      <Badge 
                        variant="outline" 
                        className="text-xs ml-2"
                        style={{ borderColor: URGENCY_COLORS[task.urgency], color: URGENCY_COLORS[task.urgency] }}
                      >
                        {task.urgency}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('tasks.noData')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
